import { useCallback, type ReactNode, type DragEvent } from 'react';
import { makeStyles } from '@fluentui/react-components';
import { extractDroppedFiles } from '@/services/office/dragDrop';
import type { DropTarget } from '@/services/office/dropHitTest';
import { addBreadcrumb } from '@/services/breadcrumbs/breadcrumbs';
import { useDropFiler } from '@/features/file-mail/useDropFiler';
import { useMailDropStore } from '@/stores/mailDropStore';

const useStyles = makeStyles({
  root: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
});

interface Props {
  children: ReactNode;
  /**
   * Where a drop that misses every folder/project lands. On the folder-detail
   * page this is the current folder/root; on the home tree it is null — a drop
   * outside any project files nowhere (no fallback to a selected folder).
   */
  fallbackTarget: DropTarget | null;
}

// Pane-level drop fallback. A FolderRow or ContainerSection under the cursor
// handles and stops its own drop; this root only catches drops that land
// outside any of them. In the home tree `fallbackTarget` is null, so such a
// drop is a no-op; on the detail page it files into the current folder/root.
export function DropZone({ children, fallbackTarget }: Props) {
  const styles = useStyles();
  const { fileFiles } = useDropFiler();
  const setDrag = useMailDropStore((s) => s.setDrag);
  const endDrag = useMailDropStore((s) => s.endDrag);

  const onDragOver = useCallback(
    (e: DragEvent) => {
      if (!e.dataTransfer.types.includes('Files')) return;
      e.preventDefault();
      // Reaching the root means the cursor is over blank space outside any
      // folder/project (FolderRow / ContainerSection stop propagation for their
      // own hovers). Mark no specific target — the detail-page overlay keys off
      // `dragging && target===null`, the home tree shows nothing.
      setDrag(null);
    },
    [setDrag],
  );

  const onDragLeave = useCallback(
    (e: DragEvent) => {
      // Only clear when the drag actually leaves the pane, not when it crosses
      // between child elements inside it.
      if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
      endDrag();
    },
    [endDrag],
  );

  const onDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      endDrag();
      const files = extractDroppedFiles(e.nativeEvent);
      if (files.length === 0) return;
      addBreadcrumb({
        category: 'office.event',
        message: 'drop-pane-fallback',
        data: { fileCount: files.length, kind: fallbackTarget?.kind ?? 'none' },
      });
      void fileFiles(files, fallbackTarget);
    },
    [fallbackTarget, fileFiles, endDrag],
  );

  return (
    <div
      className={styles.root}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {children}
    </div>
  );
}
